"use client";

import clsx from "clsx";
import Link from "next/link";
import type { Session } from "next-auth";
import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cva, type VariantProps } from "class-variance-authority";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";

import { useToast } from "@/components/ui/use-toast";
import { getPreviewMedia, getThumbnail, canViewPremiumBlogArticle, slugify } from "@/lib/utils";
import { type BlogArticle } from "@/types";

import MediaCard from "@/components/cards/MediaCard";
import { Card, CardTitle, CardContent } from "@/components/ui/card";

const cardVariants = cva("flex flex-col rounded-lg !shadow-custom", {
	variants: {
		intent: {
			grid: "h-[22rem]",
			carousel: "h-96 sm:w-[22rem]",
		},
	},
	defaultVariants: { intent: "grid" },
});

const previewVariants = cva("relative w-full rounded-t-lg p-0", {
	variants: {
		intent: {
			grid: "h-52",
			carousel: "h-56",
		},
	},
	defaultVariants: { intent: "grid" },
});

type Props = VariantProps<typeof cardVariants> & {
	data: BlogArticle;
	session: Session | null;
	favorite?: {
		isFavorite: boolean;
		isLoading: boolean;
		isUpdating: boolean;
		onClick: (id: BlogArticle) => Promise<void>;
	};
	expansion?: {
		/**
		 * Configures the base path used when the card is clicked.
		 */
		basePathname: string;
	};
};

export default function ArticleCard({
	session,
	data,
	favorite,
	intent = "grid",
	expansion = { basePathname: "/blogs" },
}: Props) {
	const { toast } = useToast();

	const media = useMemo(
		() => getPreviewMedia(data.media, data.thumbnail, data.thumbnailDisplayUrl),
		[data.media, data.thumbnail, data.thumbnailDisplayUrl],
	);
	const thumbnail = getThumbnail(data.thumbnail);

	const isBlogPremiumButNotUser =
		data.isPremium &&
		!canViewPremiumBlogArticle({
			isPremium: data.isPremium,
			planDuration: session?.user.plan.duration,
			role: session?.user.role,
		});

	// Use database slug if available, fallback to ID for backward compatibility
	const href = data.slug 
		? `${expansion.basePathname}/${data.slug}` 
		: `${expansion.basePathname}/${data.id}/${slugify(data.title)}`;

	const mediaCardChild = (
		<MediaCard
			data={media}
			className="contents"
			childClassName="rounded-t-lg size-full object-cover"
			thumbnail={thumbnail}
			disablePlayback={isBlogPremiumButNotUser}
			altText={data.title}
		/>
	);

	/** Until saved-list has loaded, show the default (unsaved) heart — no separate loading UI. */
	const heartLooksSaved = favorite && !favorite.isLoading && favorite.isFavorite;

	return (
		<Card className={cardVariants({ intent, className: "relative" })}>
			{media?.type === "VIDEO" ? (
				<div className={previewVariants({ intent })}>{mediaCardChild}</div>
			) : (
				<Link
					href={media?.type === "VIDEO" ? "" : href}
					className={previewVariants({ intent })}
				>
					{mediaCardChild}
				</Link>
			)}

			{favorite && (
				<div className="absolute right-2 top-2">
					<button
						type="button"
						disabled={favorite.isLoading || favorite.isUpdating}
						aria-busy={favorite.isUpdating}
						onClick={() => favorite.onClick(data)}
						className={clsx(
							"-mt-0.5 transition-all",
							favorite.isLoading || favorite.isUpdating
								? "cursor-wait"
								: "hover:scale-110",
						)}
					>
						<FontAwesomeIcon
							size="lg"
							icon={solidHeart}
							className={
								heartLooksSaved
									? "text-red-500"
									: "text-secondary opacity-70 transition-colors hover:opacity-90"
							}
							style={
								heartLooksSaved ? { color: "#ef4444" } : { color: "rgba(7, 168, 188, 0.7)" }
							}
						/>
					</button>
				</div>
			)}

			<Link href={href} className="contents">
				<div className="inline-flex h-10 items-center justify-between px-4 text-xsm">
					<span className="text-[11px] text-muted-foreground">
						{data.blogCategories && data.blogCategories.length > 0
							? data.blogCategories
							.slice(0, 2)
							.map((x) => x.name)
									.join(" | ")
							: "Uncategorized"}
					</span>
					<div
						className={clsx(
							"flex h-6 w-20 items-center justify-center rounded-full font-medium uppercase",
							data.isPremium ? "bg-black text-tertiary" : "bg-tertiary text-white",
						)}
					>
						{data.isPremium ? "Premium" : "Free"}
					</div>
				</div>

				<CardTitle
					className={clsx("inline-flex flex-1 px-4 text-sm3 font-medium leading-snug sm:text-sm", {
						"pt-1": intent === "grid",
						"pt-2": intent === "carousel",
					})}
				>
					{data.title}
				</CardTitle>

				<CardContent className="inline-flex h-6 items-center justify-end px-4 text-[11px] text-grey-foreground">
					<span>{data.readingTime}</span>
				</CardContent>
			</Link>
		</Card>
	);
}
